import { AbstractDisplayObjectClip } from "./AbstractDisplayObjectClip";
import { IGoodgameClip } from "./IGoodgameClip";
import { GoodgameBitmapEngine } from "../GoodgameBitmapEngine";
import { ClipLayerInfo } from "../analyse/ClipLayerInfo";
import { ClipColor } from "../components/ClipColor";
import { PrioritySignal } from "signals.js";
import { LoadingItem } from "../../loader/LoadingItem";
import { getDefinitionByNameFromLibrary } from "@goodgamestudios/createjs-utilities";

import Container = createjs.Container;
import Event = createjs.Event;
import MovieClip = createjs.MovieClip;
import Bitmap = createjs.Bitmap;

export class GoodgameDisplayObjectClipExternal extends AbstractDisplayObjectClip implements IGoodgameClip {
    /** path of the external .swf file */
    private sourcePath: string;
    /** is true when the source is loaded */
    private _isLoaded: boolean = false;
    /** priority of loading */
    private priority: number;

    private static EM_WALKERS = /(soldier_)|(male_(classic|dessert|eiland|icecream|volcano)|farhat_classic)/i;

    /** is true when the source is loaded */
    public get isLoaded(): boolean {
        return this._isLoaded;
    }

    /** sourceClassName of the DisplayObject */
    private sourceClassName: string;

    /** signal that actual loading queue is finished for the first time. name of this loader will be a parameter*/
    private _clipLoaded: PrioritySignal;

    /** signal that actual loading queue is finished for the first time. name of this loader will be a parameter*/
    public get clipLoaded(): PrioritySignal {
        return this._clipLoaded;
    }

    private placeholderClass: Object;

    private onFrameChangeCallback: Function;

    private _bitmapClone: Bitmap[] = [];

    private _delayLoadingUntilFree: boolean; // only load when nothing else loads

    constructor(clipClassName: string, swfSourcePath: string, colorData: ClipColor[] = null, fps: number = 24, autoPlay: boolean = true, placeholderClass: Object = null, hasSubLayer: boolean = false, priority: number = 1, animationColorChange: boolean = false, delayLoadingUntilFree:boolean = false) {
        super();
        this._clipLoaded = new PrioritySignal(GoodgameDisplayObjectClipExternal);
        this.animationManager = GoodgameBitmapEngine.instance.animationManager;
        this.sourceClassName = clipClassName;
        this.sourcePath = swfSourcePath;
        this.clipColor = colorData;
        this.targetFps = fps;
        this.priority = priority;
        this._running = autoPlay;
        this.hasSubLayer = hasSubLayer;
        this._delayLoadingUntilFree = delayLoadingUntilFree;
        this.animationColorChange = animationColorChange;
        this._startFrame = 1;
        this.placeholderClass = placeholderClass;

        if ( this._delayLoadingUntilFree ) {
            this.delayLoading(); // try 1-2 frames later in case we're adding a batch of assets in this frame
        } else {
            this.initLoading();
        }

    }

    // check if it's possible to load and start loading
    private initLoading(): void {
        // either load directly
        if ( this.canStartLoading() ) {
            this.loadClip();
        } else {
            // or wait until capacity is available
            GoodgameBitmapEngine.bulkLoader.addEventListener( Event.COMPLETE, this.bindFunction( this.delayLoading ) );
        }
    }

    // if delayed is true, check if this is a good time
    private canStartLoading(): boolean {
        // if not delayed, we can start loading now
        if ( !this._delayLoadingUntilFree ) {
            return true;
        }
        // if already loaded, we can use it now
        if ( GoodgameBitmapEngine.bulkLoader.isLoaded( this.sourcePath ) ) {
            return true;
        }

        // if delayed, only load if loader is free
        return GoodgameBitmapEngine.bulkLoader.isFinished;
    }

    // when we are waiting for free space and the bulkloader finished something... lets retry in a frame or two
    private delayLoading(): void {
        GoodgameBitmapEngine.bulkLoader.removeEventListener( Event.COMPLETE, this.bindFunction( this.delayLoading ) );
        if ( GoodgameBitmapEngine.bulkLoader.isLoaded( this.sourcePath ) ) {
            this.initLoading();
        } else {
            setTimeout(this.bindFunction(this.initLoading), 30);
        }
    }

    private loadClip(): void {
        let assetId = this.sourcePath.substring(this.sourcePath.lastIndexOf("/") + 1, this.sourcePath.length);
        let loadingItem: LoadingItem = GoodgameBitmapEngine.bulkLoader.addAssetBundle(this.sourcePath, {
            id: assetId.replace(/--\d+/, ""), // remove version for id
            priority: this.priority
        });

        // let loadingItem: LoadingItem = GoodgameBitmapEngine.bulkLoader.add(this.sourcePath, { id: this.sourcePath, context: context, priority: this.priority });
        if (loadingItem._isLoaded) {
            this.onLoaded(null);
        } else {
            if (this.placeholderClass) {
                this.initCurrentClip(this.placeholderClass);
            }
            loadingItem.addEventListener(Event.COMPLETE, this.bindFunction(this.onLoaded));
        }
        GoodgameBitmapEngine.bulkLoader.start(GoodgameBitmapEngine.numConnections);
    }

    // only include walkers animation
    private isPureMoiveClip(className: string): boolean {
        return GoodgameDisplayObjectClipExternal.EM_WALKERS.test(className);
    }

    private onLoaded(event: Event): void {
        if (this.currentDisplayObject) {
            this.removeChild(this.currentDisplayObject);
        }
        let bitmaps: Bitmap[];
        if (bitmaps = GoodgameBitmapEngine.cacheController.bitmapCache.get(this.sourceClassName)) {
            this.recycleAsset = false;
            this.currentDisplayObject = new MovieClip();
            this.currentDisplayObject.name = this.sourceClassName;
            if (this._running) {
                this.play();
            } else {
                this.stop();
            }
            (this.currentDisplayObject as MovieClip).children.push(bitmaps[0]);
            this.animationLength = this._endFrame = bitmaps.length;
            this.refreshBounds(this.currentDisplayObject);

            this.addChild(this.currentDisplayObject);
        } else {
            let sourceClass: any;
            sourceClass = (<Class>getDefinitionByNameFromLibrary(this.sourceClassName));
            if (!sourceClass) { return; }

            this.initCurrentClip(sourceClass);
            if (this.currentDisplayObject instanceof MovieClip
                && this.isPureMoiveClip(this.sourceClassName)
                && !GoodgameBitmapEngine.cacheController.bitmapCache.get(this.sourceClassName)) {
                GoodgameBitmapEngine.cacheController.bitmapCache.set(this.sourceClassName, this.convertMovieClip(this.currentDisplayObject as MovieClip));
                this.addChild(this.currentDisplayObject);
            }
        }

        this._isLoaded = true;
        this._clipLoaded.dispatch(this);
    }

    public get clipLayerInfo(): ClipLayerInfo {
        if (!this._clipLayerInfo && this._isLoaded) {
            this._clipLayerInfo = new ClipLayerInfo((<Container>this.currentDisplayObject));
        }
        return this._clipLayerInfo;
    }

    protected jumpToFrame(frame: number): void {
        let bitmaps: Bitmap[], currentBitmapFrame: Bitmap;
        if (this.currentDisplayObject && (bitmaps = GoodgameBitmapEngine.cacheController.bitmapCache.get(this.sourceClassName))) {
            if (this._bitmapClone.length === 0) {
                (this.currentDisplayObject as Container).removeAllChildren();
            }
            (this.currentDisplayObject as Container).children.splice(0, 1);
            currentBitmapFrame = this._bitmapClone[ frame ] || (this._bitmapClone[ frame ] = bitmaps[frame - 1].clone());
            if (!currentBitmapFrame.parent) {
                currentBitmapFrame.parent = this.currentDisplayObject as Container;
            }
            (this.currentDisplayObject as Container).children.push(currentBitmapFrame);
        } else {
            super.jumpToFrame(frame);
        }

        if (this.onFrameChangeCallback) {
            this.onFrameChangeCallback();
        }
    }

    public dispose(): void {
        super.dispose();
        this._clipLoaded.removeAll();
    }

    public setOnFrameChangeCallback(fun: Function): void {
        this.onFrameChangeCallback = fun;
    }

}
